import { UseQueryOptions } from '@tanstack/react-query';
import { MouseEvent } from 'react';

import { GlobalAccount, GUID, OpportunityView, PersonOrg, TaskView } from '@ndvr/customer-api';
import { AccountGroup } from '@ndvr/portfolio-api';

import { FORMER_STAFF, useInternalUserProfilesQuery, useKnownActorName } from '@/modules/internal-user-queries';
import { getHouseholdName, getPersonOrgName, useHouseholdRoutePaths } from 'utils';
import { getServiceIdentityName } from 'utils/service-identities';
import { useAllCommandCenterHouseholds, useAllHouseholdMap, useGlobalAccount } from 'views/household/household-queries';
import { useOpportunity } from 'views/opportunities/opportunity-queries';
import { usePersonOrg } from 'views/person/person-queries';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useAccount } from 'views/portfolio/account-queries';
import { useTaskView } from 'views/tasks/task-queries';
import { TextLink } from 'widgets';

const LOADING_TEXT = 'Loading...';

const QUERY_OPTIONS: Partial<UseQueryOptions<any>> = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
};

export const HouseholdNameLink = ({ householdGUID }: { householdGUID: GUID<GlobalAccount> }) => {
    const { PortfolioOverviewTabRoutePath } = useHouseholdRoutePaths();
    const { data: household, isPending } = useGlobalAccount(householdGUID, QUERY_OPTIONS);
    return (
        <TextLink to={PortfolioOverviewTabRoutePath(householdGUID)} onClick={(e: MouseEvent) => e.stopPropagation()}>
            {isPending ? LOADING_TEXT : getHouseholdName(household)}
        </TextLink>
    );
};

export const HouseholdLink = ({ householdGUID }: { householdGUID: GUID<GlobalAccount> }) => {
    const householdMap = useAllHouseholdMap();
    const { isPending } = useAllCommandCenterHouseholds();
    const { PortfolioOverviewTabRoutePath } = useHouseholdRoutePaths();
    return (
        <TextLink to={PortfolioOverviewTabRoutePath(householdGUID)} onClick={(e: MouseEvent) => e.stopPropagation()}>
            {isPending ? LOADING_TEXT : getHouseholdName(householdMap.get(householdGUID))}
        </TextLink>
    );
};

export const PersonOrgNameLink = ({ personOrgGUID }: { personOrgGUID: GUID<PersonOrg> }) => {
    const serviceName = getServiceIdentityName(personOrgGUID);
    const { data: personOrg, isPending } = usePersonOrg(serviceName ? undefined : personOrgGUID, QUERY_OPTIONS);
    if (serviceName) {
        return <>{serviceName}</>;
    }
    return (
        <TextLink to={`/person/${personOrgGUID}`} onClick={(e: MouseEvent) => e.stopPropagation()}>
            {isPending ? LOADING_TEXT : getPersonOrgName(personOrg)}
        </TextLink>
    );
};

/**
 * Names whoever acted on a record. Service identities and active staff are named as plain text; anyone else is looked up
 * as a person and linked. A GUID that is neither belongs to staff who have since left: their profiles are not readable
 * here, and their PersonOrg mirrors are gone.
 */
export const StaffOrPersonName = ({ guid }: { guid: GUID<PersonOrg> }) => {
    const knownName = useKnownActorName()(guid);
    const { isPending: isStaffPending } = useInternalUserProfilesQuery();
    const lookUpPerson = !knownName && !isStaffPending;
    const { data: personOrg, isPending: isPersonPending } = usePersonOrg(
        lookUpPerson ? guid : undefined,
        QUERY_OPTIONS,
    );

    if (knownName) {
        return <>{knownName}</>;
    }
    if (isStaffPending || isPersonPending) {
        return <>{LOADING_TEXT}</>;
    }
    if (!personOrg) {
        return <>{FORMER_STAFF}</>;
    }
    return (
        <TextLink to={`/person/${guid}`} onClick={(e: MouseEvent) => e.stopPropagation()}>
            {getPersonOrgName(personOrg)}
        </TextLink>
    );
};

export const AccountGroupNameLink = ({ accountGroupGUID }: { accountGroupGUID: GUID<AccountGroup> }) => {
    const { data: profile, isPending } = useAccountGroupProfileWithAumIncluded(accountGroupGUID, QUERY_OPTIONS);
    return (
        <TextLink to={`/account-group/${accountGroupGUID}`} onClick={(e: MouseEvent) => e.stopPropagation()}>
            {isPending ? LOADING_TEXT : profile?.accountGroup.accountGroupName.plainText || 'Unnamed Portfolio'}
        </TextLink>
    );
};

export const OpportunityNameLink = ({ opportunityGUID }: { opportunityGUID: GUID<OpportunityView> }) => {
    const { HouseholdIndividualPageRoutePath } = useHouseholdRoutePaths();
    const { data: opportunity, isPending } = useOpportunity(opportunityGUID);
    const to = opportunity ? HouseholdIndividualPageRoutePath(opportunity.householdGUID, 'opportunities') : '#';
    return (
        <TextLink to={to} onClick={(e: MouseEvent) => e.stopPropagation()}>
            {isPending ? LOADING_TEXT : opportunity?.name || 'Unnamed Opportunity'}
        </TextLink>
    );
};

export const TaskNameLink = ({ taskGUID }: { taskGUID: GUID<TaskView> }) => {
    const { data: task, isPending } = useTaskView(taskGUID);
    return (
        <TextLink to={`/tasks/${taskGUID}`} onClick={(e: MouseEvent) => e.stopPropagation()}>
            {isPending ? LOADING_TEXT : task?.title || 'Unnamed Task'}
        </TextLink>
    );
};

export const AccountNameLink = ({ accountGUID }: { accountGUID: GUID<AccountGroup> }) => {
    const { data: account, isPending } = useAccount(accountGUID, QUERY_OPTIONS);
    return (
        <TextLink to={`/account/${accountGUID}`} onClick={(e: MouseEvent) => e.stopPropagation()}>
            {isPending ? LOADING_TEXT : account?.accountName.plainText || 'Unnamed Account'}
        </TextLink>
    );
};
